/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import * as ReactOidcContext from "react-oidc-context";
import * as FeatureFlag from "@shared/utils/feature-flag";
import { useAuth } from "@context/authentication";
import { LoadingPage } from "@pages/other/LoadingPage";
import { persistProtectedLandingReturnPath } from "./protected-landing-persistence";
import { resolveOidcRedirectUri } from "@shared/utils/native-oidc";

const AUTH_REDIRECT_TIMEOUT_MS = 30000;

const RedirectingFallback: React.FC = () => {
  const redirectedRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (redirectedRef.current) return;
      redirectedRef.current = true;
      window.location.replace('/');
    }, AUTH_REDIRECT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  return <LoadingPage />;
};

const MockAuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !redirectedRef.current) {
      redirectedRef.current = true;
      window.location.replace('/');
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  if (auth.isLoading || !auth.isAuthenticated) {
    return <LoadingPage />;
  }

  return <>{children}</>;
};

export const withAuthRedirect = <P extends object>(Component: React.ComponentType<P>) => {
  const ProtectedComponent = (props: P) => <Component {...props} />;
  ProtectedComponent.displayName = Component.displayName || Component.name || 'Component';

  const MockInner = (props: P) => (
    <MockAuthGuard>
      <ProtectedComponent {...props} />
    </MockAuthGuard>
  );

  const OidcInner = (props: P) => {
    // Resolve `withAuthenticationRequired` at RENDER time via the namespace object,
    // NOT at `withAuthRedirect()` call time. Under Vitest `isolate: false`, sibling
    // test files that `vi.unmock('react-oidc-context')` (real module) and files that
    // `vi.mock` it share one worker module registry; a static top-level binding would
    // freeze whichever variant loaded first. Reading `ReactOidcContext.withAuthenticationRequired`
    // per render (memoized per component identity) always uses the currently-active
    // implementation. In production this is a stable single import — a harmless indirection.
    const AuthenticatedOnly = useMemo(
      () =>
        ReactOidcContext.withAuthenticationRequired(ProtectedComponent, {
          OnRedirecting: () => <RedirectingFallback />,
          signinRedirectArgs: {
            redirect_uri: resolveOidcRedirectUri(),
          },
        }),
      [],
    );
    return <AuthenticatedOnly {...props} />;
  };

  const Wrapped = (props: P) => {
    useLayoutEffect(() => {
      persistProtectedLandingReturnPath();
    }, []);
    // Read the flag at RENDER time (not module-load time). Under Vitest
    // `isolate: false` the `FF_MOCK_AUTH` binding is last-writer-wins per worker;
    // a module-level `const USE_MOCK_AUTH = FF_MOCK_AUTH` would freeze whichever
    // value loaded first and cause the wrong guard to render. The namespace read
    // always reflects the currently-active flag. In production this is a stable
    // constant — a harmless indirection.
    return FeatureFlag.FF_MOCK_AUTH ? <MockInner {...props} /> : <OidcInner {...props} />;
  };
  Wrapped.displayName = `withAuthRedirect(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
};
